<!--
	@component
	The form component enables automatic schema validation on it's children if the `schema` prop is assigned (and the child components has support for consuming the `form` context).
  
  How to consume/hook into the form context from a child component:
  @example
  ```
  <script lang="ts">
    import { getContext } from 'svelte'
    import { contextKey } from '$lib/contexts/form'
    import type { FormContext } from '$lib/types/form'
  
    const { formFieldErrors, submitting, getFieldConstraints, validateField } = <FormContext>getContext(contextKey)
  
    $: errors = name && $formFieldErrors?.[name]
  </script>
  ```
-->
<script lang="ts">
  import { writable, derived } from 'svelte/store'
  import { setContext, tick } from 'svelte'
  import { page } from '$app/stores'
  import { applyAction, enhance } from '$app/forms'
  import { contextKey } from '../../contexts/form'
  import Alert from './Alert.svelte'
  import { utils } from '../../utils'
  import type { FormContext } from '../../types/form'
  import type { SafeParseReturnType, ZodSchema } from 'zod'
  import type { HTMLFormAttributes } from 'svelte/elements'
  import type { Language } from '../../..'

  /** Multiple Zod validation schemas when using multiple named actions */
  export let multiSchema: Record<string, ZodSchema> | undefined = undefined
  /** Zod validation schema enabling automatic client side validation (remember to add `name` attributes to form fields). */
  export let schema: ZodSchema | undefined = undefined
  /** Used to invoke named form actions (e.g. `?/register`) or form actions from other pages (e.g. `/login`). */
  export let action: string | undefined = undefined
  /** Whether to include directories in the result. */
  export let resetOnFormSubmission = false
  /** Encoding type, only needed when using input type file */
  export let enctype: HTMLFormAttributes['enctype'] = undefined
  /** Expose submitting */
  export let submitting: FormContext['submitting'] = writable(false)

  export function requestSubmit() {
    return form?.requestSubmit()
  }

  export function reset() {
    return form?.reset()
  }

  export function getForm() {
    return form
  }

  let form: HTMLFormElement | undefined = undefined
  let hasSubmitted = !!$page.form?.fieldErrors
  let initialFormData: Record<string, (string | File)[] | string | File>
  const vercelLimit = 4
  const maxSize = vercelLimit * 1024 * 1024 // 4MB, due to Vercel limitations
  let maxSizeAlert = false
  let maxSizeAlertElement: HTMLDivElement | undefined = undefined
  let genericAlertElement: HTMLDivElement | undefined = undefined
  let errorElements: Element[] = []

  const GENERIC_FORM_ERROR = {
    en: 'Something went wrong while validating the form.',
    sv: 'Något gick fel när formuläret validerades.',
    nb: 'Noe gikk galt under valideringen av skjemaet.',
    da: 'Noget gik galt under valideringen af formularen.',
  } as const

  $: lang = ($page.params.lang || 'en') as Language

  $: errorNames = Object.keys($formFieldErrors || {})

  $: errorElements = errorNames
    .map((name) => form?.querySelector(`[name="${name}"]`))
    .filter((element) => element !== null && element !== undefined)

  $: onlyHiddenInvalids =
    errorElements.length > 0 &&
    errorElements.every((element) => element.hasAttribute('hidden'))

  $: if (onlyHiddenInvalids) {
    // Log form validation errors for hidden elements
    console.error('Form validation failed with', $formFieldErrors)
  }

  $: if (genericAlertElement) {
    genericAlertElement.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    })
  }

  $: if (maxSizeAlertElement) {
    maxSizeAlertElement.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    })
  }

  const formFieldErrors: FormContext['formFieldErrors'] = writable(
    $page.form?.fieldErrors,
  )

  const internalDirtyFields: FormContext['dirtyFields'] = writable([])
  /** Expose dirty fields as a readable store */
  export const dirtyFields = derived(internalDirtyFields, ($fields) => $fields)

  const getFieldConstraints = (fieldName?: string) => {
    if (schema && fieldName) {
      return utils.generateFieldConstraints(schema, fieldName)
    }
  }

  const validateAllFields = (formData: FormData, action: URL) => {
    if (schema || multiSchema) {
      const data = utils.getFormData(formData)
      const actionKey = action.search.split('/')[1]
      let parsed: SafeParseReturnType<unknown, unknown> | undefined

      if (schema) {
        parsed = schema.safeParse(data)
      } else if (multiSchema && actionKey) {
        parsed = multiSchema[actionKey]?.safeParse(data)
      }

      if (parsed && !parsed.success) {
        formFieldErrors.set(parsed.error.formErrors.fieldErrors)
      } else {
        formFieldErrors.set(undefined)
      }
    }
  }

  const validateField = (event: Event) => {
    if (schema) {
      const data = Object.fromEntries(new FormData(form))
      const parsed = schema.safeParse(data)
      if (!parsed.success) {
        const newErrors = parsed.error.formErrors.fieldErrors
        const name = (
          event.target as
            | HTMLInputElement
            | HTMLSelectElement
            | HTMLTextAreaElement
        )?.name

        formFieldErrors.update((currentErrors) => {
          return {
            /**
             * Validation errors should cleared as soon as they are resolved, even if it isn't the currently edited field (as field validations can be dependant on values from other fields, e.g. email and email confirmation fields).
             * It is also important to continuously clear resolved errors after each value is changed (using input/change events) as non-screen-reader users are unlikely to tab away from the input or click elsewhere on the page before clicking submit.
             */
            ...utils.clearAllResolvedErrors({ currentErrors, newErrors }),
            /**
             * Only add new errors on blur events after the first submission (so the user isn't overloaded with too much information).
             */
            ...(hasSubmitted &&
              event.type === 'blur' && {
                [name]: newErrors[name],
              }),
          }
        })
      } else {
        formFieldErrors.set(undefined)
      }
    }
  }

  const focusFirstError = async () => {
    /**
     * Focus on first error in form (requires that element has `data-invalid` attribute set).
     */
    await tick()
    const firstError = form?.querySelector('[data-invalid]') as HTMLElement

    const expansionPanel = utils
      .getAncestors(firstError)
      .find((item) => item.tagName === 'DETAILS') as HTMLDetailsElement

    if (expansionPanel) {
      expansionPanel.open = true
    }

    firstError?.focus()
  }

  /**
   * Update/replace all form field errors when submitting the form.
   */
  $: {
    formFieldErrors.set($page.form?.fieldErrors)
    if ($page.form?.fieldErrors) {
      focusFirstError()
    }
  }

  setContext(contextKey, {
    formFieldErrors,
    dirtyFields: internalDirtyFields,
    schema,
    submitting,
    getFieldConstraints,
    validateField,
  })

  $: initialFormData = utils.getFormData(new FormData(form))

  function handleInput(event: Event) {
    const inputEvent = event as InputEvent
    const typedTarget = inputEvent.target as
      | HTMLInputElement
      | HTMLSelectElement
      | HTMLTextAreaElement

    const newValue = typedTarget.value
    const name = typedTarget.name
    const initialValue = initialFormData[name]

    if ($internalDirtyFields) {
      if (newValue !== initialValue && name) {
        internalDirtyFields.set([...new Set([...$internalDirtyFields, name])])
      } else {
        internalDirtyFields.set(
          $internalDirtyFields.filter((item: string) => item !== name),
        )
      }
    }

    checkFormBodySize()
  }

  function checkFormBodySize() {
    const formData = new FormData(form)
    let size = 0

    for (const item of formData.entries()) {
      if (typeof item[1] === 'string') {
        size += new Blob([item[1]]).size
      } else {
        size += item[1].size
      }
    }

    if (size > maxSize) {
      maxSizeAlert = true
    } else {
      maxSizeAlert = false
    }
  }

  function handleInvalid(event: Event) {
    const element = event.target as
      | HTMLInputElement
      | HTMLSelectElement
      | HTMLTextAreaElement

    hasSubmitted = true

    const expansionPanel = utils
      .getAncestors(element)
      .find((item) => item.tagName === 'DETAILS') as HTMLDetailsElement

    if (expansionPanel) {
      expansionPanel.open = true
    }

    element.focus()
  }
</script>

<form
  method="POST"
  {action}
  bind:this={form}
  use:enhance={({ action, formData, cancel }) => {
    submitting.set(true)
    hasSubmitted = true
    validateAllFields(formData, action)

    if ($formFieldErrors) {
      cancel()
      submitting.set(false)
      focusFirstError()
    }

    return async ({ result, update }) => {
      await applyAction(result)
      await update({ reset: resetOnFormSubmission })
      submitting.set(false)
    }
  }}
  {enctype}
  on:formdata
  on:invalid|capture={handleInvalid}
  on:input={handleInput}
>
  {#if $page.form && 'error' in $page.form}
    <Alert icon="ph-warning-circle">
      {$page.form.error.message ? $page.form.error.message : $page.form.error}
    </Alert>
  {:else if $formFieldErrors && Object.keys($formFieldErrors).length > 0 && onlyHiddenInvalids}
    <div bind:this={genericAlertElement}>
      <Alert icon="ph-warning-circle">
        {GENERIC_FORM_ERROR[lang]}
      </Alert>
    </div>
  {/if}
  {#if maxSizeAlert}
    <div bind:this={maxSizeAlertElement}>
      <Alert>
        <p>
          {`The total max size of the form should not exceed ${vercelLimit}MB. Please remove some files and try again.`}
        </p>
      </Alert>
    </div>
  {/if}

  <slot submitting={$submitting} />
</form>

<style lang="scss">
  form {
    display: flex;
    flex-direction: column;
    gap: var(--field-gap);
  }
</style>
